/**
 * 日期范围验证工具
 */

/**
 * 验证日期范围是否有效
 * @param {Object} dateRange - 包含startDate和endDate的对象
 * @param {string} dateRange.startDate - 开始日期 (YYYY-MM-DD格式)
 * @param {string} dateRange.endDate - 结束日期 (YYYY-MM-DD格式)
 * @param {Object} options - 配置选项
 * @param {number} options.maxDays - 最大允许的天数范围，默认90天
 * @param {boolean} options.allowSameDay - 是否允许同一天，默认true
 * @returns {Object} 验证结果 {isValid: boolean, message: string, correctedDateRange: Object}
 */
export function validateDateRange1(dateRange, options = {}) {
  const { maxDays = 90, allowSameDay = true } = options;
  const { startDate, endDate } = dateRange;
  
  // 检查日期是否为空
  if (!startDate || !endDate) {
    return {
      isValid: false,
      message: '请选择开始日期和结束日期',
      correctedDateRange: dateRange
    };
  }
  
  // 解析日期
  const start = new Date(startDate);
  const end = new Date(endDate);
  
  // 检查日期是否有效
  if (isNaN(start.getTime()) || isNaN(end.getTime())) {
    return {
      isValid: false,
      message: '日期格式无效',
      correctedDateRange: dateRange
    };
  }
  
  // 检查开始日期是否晚于结束日期
  if (start > end) {
    // 自动交换日期
    const correctedDateRange = {
      startDate: endDate,
      endDate: startDate
    };
    
    return {
      isValid: false,
      message: '开始日期不能晚于结束日期，已自动交换',
      correctedDateRange,
      autoCorrected: true
    };
  }
  
  // 计算天数差
  const diffTime = Math.abs(end - start);
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  
  // 检查是否同一天（如果不允许）
  if (!allowSameDay && diffDays === 0) {
    return {
      isValid: false,
      message: '开始日期和结束日期不能相同',
      correctedDateRange: dateRange
    };
  }
  
  // 检查是否超过最大天数
  if (diffDays > maxDays) {
    // 自动调整结束日期
    const newEnd = new Date(start);
    newEnd.setDate(start.getDate() + maxDays);
    const correctedEndDate = newEnd.toISOString().slice(0, 10);
    
    return {
      isValid: false,
      message: `查询时间范围不能超过${maxDays}天，已自动调整`,
      correctedDateRange: {
        startDate,
        endDate: correctedEndDate
      },
      autoCorrected: true
    };
  }
  
  return {
    isValid: true,
    message: '日期范围有效',
    correctedDateRange: dateRange
  };
}

/**
 * 验证日期范围（最大90天），自动修正不合法日期
 * @param {Object} dateObj - 日期对象（包含startDate、endDate，与Vue的query一致）
 * @param {Function} showMessage - Vue的提示方法（如this.$message/ElMessage）
 * @param {number} [maxDays=90] - 最大允许天数，可选，默认90天
 * @returns {boolean} - 日期非空且完成验证/修正返回true，日期为空返回false
 */
export function validateDateRange(dateObj, showMessage, maxDays = 90) {
  // 唯一返回false的情况：日期为空（未选择完整）
  if (!dateObj.startDate || !dateObj.endDate) {
    console.log('日期为空，验证未执行');
    return false;
  }
  
  const start = new Date(dateObj.startDate);
  const end = new Date(dateObj.endDate);
  let isModified = false; // 标记是否执行了修正

  // 规则1：开始日期晚于结束日期 → 修正结束日期为开始日期
  if (start > end) {
    console.log('开始日期晚于结束日期，执行自动修正');
    showMessage('开始日期不能晚于结束日期');
    //开始和结束日期交换
    const dateObj1 = dateObj.startDate
    const dateObj2 = dateObj.endDate
    dateObj.startDate = dateObj2;
    dateObj.endDate = dateObj1;
    isModified = true;
  }

  // 规则2：日期范围超出最大天数 → 修正结束日期为开始日期+maxDays天
  const diffTime = Math.abs(new Date(dateObj.endDate) - start);
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  if (diffDays > maxDays) {
    console.log(`日期范围超过${maxDays}天，执行自动修正`);
    showMessage(`查询时间范围不能超过${maxDays}天，已为你自动修正`);
    const newEndDate = new Date(start);
    newEndDate.setDate(start.getDate() + maxDays);
    dateObj.endDate = newEndDate.toISOString().slice(0, 10);
    isModified = true;
  }

  // 核心逻辑：无论是否修正（只要非空），最终都返回true
  console.log(isModified ? '日期修正完成，返回true' : '日期在合法范围，返回true');
  return true;
}

/**
 * 设置默认日期范围
 * @param {number} daysBefore - 往前推的天数，默认7天
 * @returns {Object} 包含startDate和endDate的对象
 */
export function setDefaultDateRange(daysBefore = 7) {
  const endDate = new Date();
  const startDate = new Date();
  startDate.setDate(endDate.getDate() - daysBefore);
  
  return {
    startDate: startDate.toISOString().slice(0, 10),
    endDate: endDate.toISOString().slice(0, 10)
  };
}

/**
 * 格式化日期为YYYY-MM-DD
 * @param {Date|string} date - 日期对象或字符串
 * @returns {string} 格式化后的日期字符串
 */
export function formatDate(date) {
  const d = date instanceof Date ? date : new Date(date);
  if (isNaN(d.getTime())) {
    return '';
  }
  return d.toISOString().slice(0, 10);
}

// 日期格式转换：YYYY-MM-DD → YYYY/MM/DD
export function formatDateForApi(dateStr) {
  if (!dateStr) return '';
  return dateStr.replace(/-/g, '/');
}

/**
 * 获取日期范围内的所有日期
 * @param {string} startDate - 开始日期
 * @param {string} endDate - 结束日期
 * @returns {Array<string>} 日期字符串数组
 */
export function getDateRange(startDate, endDate) {
  const start = new Date(startDate);
  const end = new Date(endDate);
  const dates = [];
  
  for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
    dates.push(formatDate(d));
  }
  
  return dates;
}

/**
 * 检查是否为有效日期
 * @param {string} dateString - 日期字符串
 * @returns {boolean} 是否有效
 */
export function isValidDate(dateString) {
  const date = new Date(dateString);
  return !isNaN(date.getTime()) && dateString.length === 10;
}

/**
 * 比较两个日期范围是否相同
 * @param {Object} range1 - 第一个日期范围
 * @param {Object} range2 - 第二个日期范围
 * @returns {boolean} 是否相同
 */
export function isSameDateRange(range1, range2) {
  return range1.startDate === range2.startDate && range1.endDate === range2.endDate;
}

/**
 * 获取查询字符串表示（用于缓存比较）
 * @param {Object} dateRange - 日期范围对象
 * @returns {string} 查询字符串
 */
export function getDateRangeString(dateRange) {
  return `${dateRange.startDate || ''}_${dateRange.endDate || ''}`;
}

export default {
  
  validateDateRange1, // 验证日期范围是否有效
  formatDate, // 格式化日期为YYYY-MM-DD
  getDateRange, // 获取日期范围内的所有日期
  isValidDate, // 检查是否为有效日期
  isSameDateRange, // 比较两个日期范围是否相同
  getDateRangeString, // 获取查询字符串表示（用于缓存比较）

  setDefaultDateRange, // 设置默认日期范围
  formatDateForApi, // 日期格式转换：YYYY-MM-DD → YYYY/MM/DD
  validateDateRange,// 验证日期范围是否有效
};